// Fase 17.3, Plano 06 — editor de etapas do roteiro (D-14, D-18, D-22), compartilhado pela ficha da peça e pela tela de modelos.
// Cada etapa: serviço, UMA célula padrão do serviço, tempo padrão opcional, "consome a tinta da peça" e os insumos por peça.
// A cabine final é escolhida no Kanban entre as células do mesmo serviço (D-18).
import type { ServicoComCelulasDto } from '@pieltec/contracts';
import { Button } from '../../../components/ui/button';
import { CampoSelect } from '../../../components/ui/campo-select';
import { Checkbox } from '../../../components/ui/checkbox';
import { Input } from '../../../components/ui/input';
import { moverEtapa, novaEtapa, removerEtapa, trocarServico, validarEtapas, type EtapaEdicao, type InsumoEdicao } from '../../../lib/roteiros/etapas';

export interface EditorEtapasProps {
  readonly etapas: readonly EtapaEdicao[];
  readonly servicos: readonly ServicoComCelulasDto[];
  readonly onChange: (etapas: EtapaEdicao[]) => void;
  readonly somenteLeitura?: boolean;
  /** Mostra as mensagens de validação (a ficha só liga depois da primeira tentativa de salvar). */
  readonly mostrarErros?: boolean;
}

export const AJUDA_CELULA_PADRAO = 'A cabine final é escolhida no Kanban entre as células do mesmo serviço.';

function rotuloServico(servico: ServicoComCelulasDto): string {
  return `${servico.codigo} — ${servico.nome}`;
}

function opcoesDeCelula(servico: ServicoComCelulasDto | undefined, atual: string): { value: string; label: string }[] {
  const celulas = (servico?.celulas ?? []).filter((celula) => celula.ativo).map((celula) => ({ value: celula.codigo, label: celula.codigo === celula.nome ? celula.codigo : `${celula.codigo} — ${celula.nome}` }));
  // A célula salva pode ter saído do serviço ou estar inativa: continua visível para o PCP trocar de propósito.
  return atual !== '' && !celulas.some((opcao) => opcao.value === atual) ? [{ value: atual, label: `${atual} (fora do serviço)` }, ...celulas] : celulas;
}

function ResumoLeitura({ etapa, indice, servicos }: { readonly etapa: EtapaEdicao; readonly indice: number; readonly servicos: readonly ServicoComCelulasDto[] }) {
  const servico = servicos.find((s) => s.id === etapa.servicoId);
  return (
    <li className="flex flex-col gap-1 rounded-md border p-4">
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="font-semibold">{indice + 1}. {servico ? rotuloServico(servico) : 'Serviço'}</span>
        <span className="text-sm text-muted-foreground">Célula padrão: {etapa.celulaId || '—'}</span>
        {etapa.tempoMinutos ? <span className="text-sm text-muted-foreground">Tempo padrão: {etapa.tempoMinutos} min</span> : null}
        {etapa.usaTintaDaPeca ? <span className="text-sm text-muted-foreground">Consome a tinta da peça</span> : null}
      </div>
      {etapa.insumos.length > 0 ? (
        <span className="text-sm text-muted-foreground">Insumos por peça: {etapa.insumos.map((insumo) => `${insumo.insumoItemId} (${insumo.quantidade})`).join(', ')}</span>
      ) : null}
    </li>
  );
}

export function EditorEtapas({ etapas, servicos, onChange, somenteLeitura = false, mostrarErros = false }: EditorEtapasProps) {
  if (somenteLeitura) {
    return <ol className="flex flex-col gap-2" aria-label="Etapas do roteiro">{etapas.map((etapa, indice) => <ResumoLeitura key={etapa.chave} etapa={etapa} indice={indice} servicos={servicos} />)}</ol>;
  }

  const atualizar = (indice: number, mudanca: Partial<EtapaEdicao>): void => onChange(etapas.map((etapa, posicao) => (posicao === indice ? { ...etapa, ...mudanca } : etapa)));
  const atualizarInsumo = (indice: number, posicao: number, mudanca: Partial<InsumoEdicao>): void => {
    const etapa = etapas[indice];
    if (!etapa) return;
    atualizar(indice, { insumos: etapa.insumos.map((insumo, i) => (i === posicao ? { ...insumo, ...mudanca } : insumo)) });
  };
  const opcoesServico = servicos.map((servico) => ({ value: servico.id, label: rotuloServico(servico) }));
  const mensagens = mostrarErros ? validarEtapas(etapas) : [];
  const primeiroServico = servicos[0];

  return (
    <div className="flex flex-col gap-4">
      <ol className="flex flex-col gap-4" aria-label="Etapas do roteiro">
        {etapas.map((etapa, indice) => {
          const servico = servicos.find((s) => s.id === etapa.servicoId);
          const idBase = `etapa-${etapa.chave}`;
          return (
            <li key={etapa.chave} className="flex flex-col gap-4 rounded-md border p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-sm font-semibold">Etapa {indice + 1}</h3>
                <div className="flex flex-wrap gap-2">
                  <Button type="button" variant="outline" size="sm" disabled={indice === 0} onClick={() => onChange(moverEtapa(etapas, indice, -1))}>Subir</Button>
                  <Button type="button" variant="outline" size="sm" disabled={indice === etapas.length - 1} onClick={() => onChange(moverEtapa(etapas, indice, 1))}>Descer</Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => onChange(removerEtapa(etapas, indice))}>Remover</Button>
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="flex flex-col gap-1">
                  <label htmlFor={`${idBase}-servico`} className="text-sm font-semibold">Serviço</label>
                  <CampoSelect
                    id={`${idBase}-servico`}
                    value={etapa.servicoId}
                    placeholder="Escolha o serviço"
                    options={opcoesServico}
                    onValueChange={(valor) => {
                      const novo = servicos.find((s) => s.id === valor);
                      if (novo) onChange(etapas.map((atual, posicao) => (posicao === indice ? trocarServico(atual, novo) : atual)));
                    }}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label htmlFor={`${idBase}-celula`} className="text-sm font-semibold">Célula padrão</label>
                  <CampoSelect
                    id={`${idBase}-celula`}
                    value={etapa.celulaId}
                    placeholder="Escolha a célula"
                    options={opcoesDeCelula(servico, etapa.celulaId)}
                    aria-describedby={`${idBase}-celula-ajuda`}
                    onValueChange={(valor) => atualizar(indice, { celulaId: valor })}
                  />
                  <p id={`${idBase}-celula-ajuda`} className="text-xs text-muted-foreground">{AJUDA_CELULA_PADRAO}</p>
                </div>
                <div className="flex flex-col gap-1">
                  <label htmlFor={`${idBase}-tempo`} className="text-sm font-semibold">Tempo padrão (min)</label>
                  <Input id={`${idBase}-tempo`} inputMode="decimal" placeholder="Opcional" value={etapa.tempoMinutos} onChange={(evento) => atualizar(indice, { tempoMinutos: evento.target.value })} />
                </div>
                <div className="flex flex-col justify-end gap-1">
                  <label htmlFor={`${idBase}-tinta`} className="flex items-center gap-2 text-sm">
                    <Checkbox id={`${idBase}-tinta`} checked={etapa.usaTintaDaPeca} onCheckedChange={(marcado) => atualizar(indice, { usaTintaDaPeca: marcado === true })} />
                    Consome a tinta da peça
                  </label>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <span className="text-sm font-semibold">Insumos por peça</span>
                {etapa.insumos.length === 0 ? <p className="text-xs text-muted-foreground">Nenhum insumo nesta etapa.</p> : null}
                {etapa.insumos.map((insumo, posicao) => (
                  <div key={posicao} className="grid items-end gap-2 md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_auto]">
                    <div className="flex flex-col gap-1">
                      <label htmlFor={`${idBase}-insumo-${posicao}`} className="text-xs text-muted-foreground">Código do material</label>
                      <Input id={`${idBase}-insumo-${posicao}`} value={insumo.insumoItemId} onChange={(evento) => atualizarInsumo(indice, posicao, { insumoItemId: evento.target.value })} />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label htmlFor={`${idBase}-qtd-${posicao}`} className="text-xs text-muted-foreground">Quantidade por peça</label>
                      <Input id={`${idBase}-qtd-${posicao}`} inputMode="decimal" value={insumo.quantidade} onChange={(evento) => atualizarInsumo(indice, posicao, { quantidade: evento.target.value })} />
                    </div>
                    <Button type="button" variant="outline" size="sm" onClick={() => atualizar(indice, { insumos: etapa.insumos.filter((_, i) => i !== posicao) })}>Remover insumo</Button>
                  </div>
                ))}
                <div>
                  <Button type="button" variant="outline" size="sm" onClick={() => atualizar(indice, { insumos: [...etapa.insumos, { insumoItemId: '', quantidade: '' }] })}>Adicionar insumo</Button>
                </div>
              </div>
            </li>
          );
        })}
      </ol>
      <div>
        <Button type="button" variant="outline" disabled={!primeiroServico} onClick={() => primeiroServico && onChange([...etapas, novaEtapa(primeiroServico)])}>Adicionar etapa</Button>
      </div>
      {mensagens.length > 0 ? (
        <ul role="alert" className="flex flex-col gap-1 text-sm text-destructive">
          {mensagens.map((mensagem, indice) => <li key={`${indice}-${mensagem}`}>{mensagem}</li>)}
        </ul>
      ) : null}
    </div>
  );
}
